import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { WarehouseLayout } from '../components/common/layout/warehouseLayout';
import {
  WarehouseSignIn,
  WarehouseForgotPassword,
  WarehouseResetPassword,
  WarehousePasswordSuccess,
  WarehouseProfile,
  WarehouseOperations,
  UploadDeliveryNote,
  NoOrdersFound,
  PossibleOrders,
  OrderDetails,
  OutboundOrderDetails,
  CaptureItem,
  ItemDetails,
  Packaging,
  FullPackageAndDimensions,
  Labels,
  LabelPrint,
  RaiseClaim,
} from '../pages/warehouse';
import { ADMIN_ROUTES, WAREHOUSE_ROUTES } from '../constants/routes';
import { useWarehouseAuth } from '../hooks/warehouse';
import { isERPUser } from '../utils/routeGuards';

// Protected Route Component
interface WarehouseProtectedRouteProps {
  children: React.ReactNode;
}

const WarehouseProtectedRoute: React.FC<WarehouseProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useWarehouseAuth();

  // Check if ERP user is trying to access warehouse routes
  if (isERPUser()) {
    return <Navigate to={ADMIN_ROUTES.dashboardPage} replace />;
  }

  // Show loading while checking auth
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to={WAREHOUSE_ROUTES.warehouseSignIn} replace />;
  }

  // Check if user has warehouse access
  if (!user.isWarehouseUser) {
    return (
      <div
        style={{
          padding: '50px',
          textAlign: 'center',
          color: '#ff4d4f',
          fontSize: '16px',
        }}
      >
        <h2>Access Denied</h2>
        <p>You don&apos;t have permission to access warehouse operations.</p>
        <p>Please contact your administrator.</p>
      </div>
    );
  }

  return <WarehouseLayout>{children}</WarehouseLayout>;
};

// Public Route Component (for auth pages)
interface WarehousePublicRouteProps {
  children: React.ReactNode;
}

const WarehousePublicRoute: React.FC<WarehousePublicRouteProps> = ({ children }) => {
  // Check if ERP user is trying to access warehouse auth pages
  if (isERPUser()) {
    return <Navigate to={ADMIN_ROUTES.dashboardPage} replace />;
  }

  return <>{children}</>;
};

export const WarehouseRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes - Use relative paths */}
      <Route
        path={WAREHOUSE_ROUTES.relative.signIn}
        element={
          <WarehousePublicRoute>
            <WarehouseSignIn />
          </WarehousePublicRoute>
        }
      />

      <Route
        path={WAREHOUSE_ROUTES.relative.forgotPassword}
        element={
          <WarehousePublicRoute>
            <WarehouseForgotPassword />
          </WarehousePublicRoute>
        }
      />

      <Route
        path={WAREHOUSE_ROUTES.relative.resetPassword}
        element={
          <WarehousePublicRoute>
            <WarehouseResetPassword />
          </WarehousePublicRoute>
        }
      />

      <Route
        path={WAREHOUSE_ROUTES.relative.resetPasswordSuccess}
        element={
          <WarehousePublicRoute>
            <WarehousePasswordSuccess />
          </WarehousePublicRoute>
        }
      />

      {/* Protected Routes - Use relative paths */}
      <Route
        path={WAREHOUSE_ROUTES.relative.profile}
        element={
          <WarehouseProtectedRoute>
            <WarehouseProfile />
          </WarehouseProtectedRoute>
        }
      />

      <Route
        path={WAREHOUSE_ROUTES.relative.operations}
        element={
          <WarehouseProtectedRoute>
            <WarehouseOperations />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id"
        element={
          <WarehouseProtectedRoute>
            <OrderDetails />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id/capture"
        element={
          <WarehouseProtectedRoute>
            <CaptureItem />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id/details"
        element={
          <WarehouseProtectedRoute>
            <ItemDetails />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id/packaging"
        element={
          <WarehouseProtectedRoute>
            <Packaging />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id/full-package-dimensions"
        element={
          <WarehouseProtectedRoute>
            <FullPackageAndDimensions />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id/labels"
        element={
          <WarehouseProtectedRoute>
            <Labels />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id/label-print"
        element={
          <WarehouseProtectedRoute>
            <LabelPrint />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound/:id/raise-claim"
        element={
          <WarehouseProtectedRoute>
            <RaiseClaim />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/inbound"
        element={
          <WarehouseProtectedRoute>
            <WarehouseOperations />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/outbound"
        element={
          <WarehouseProtectedRoute>
            <WarehouseOperations />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/outbound/:id"
        element={
          <WarehouseProtectedRoute>
            <OutboundOrderDetails />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/upload-delivery-note"
        element={
          <WarehouseProtectedRoute>
            <UploadDeliveryNote />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/no-orders-found"
        element={
          <WarehouseProtectedRoute>
            <NoOrdersFound />
          </WarehouseProtectedRoute>
        }
      />
      <Route
        path="/possible-orders"
        element={
          <WarehouseProtectedRoute>
            <PossibleOrders />
          </WarehouseProtectedRoute>
        }
      />

      {/* Default redirect to inbound for warehouse users */}
      <Route index element={<Navigate to="/inbound" replace />} />

      {/* Fallback for unmatched warehouse routes */}
      <Route
        path="*"
        element={
          <WarehouseProtectedRoute>
            <div style={{ padding: '50px', textAlign: 'center' }}>
              <h2>Page Not Found</h2>
              <p>The warehouse page you&apos;re looking for doesn&apos;t exist.</p>
            </div>
          </WarehouseProtectedRoute>
        }
      />
    </Routes>
  );
};
